// @ts-strict-ignore
import React, { useState, useRef, useEffect } from 'react'
import { useIonViewDidEnter } from '@ionic/react'
import { useMediaQuery } from 'react-responsive'
import classNames from 'classnames'
import { SectionLinkGroupContainer as Container } from 'mytours-read-common'

import { Button, LinkGroupSection, Grid } from '@mytours/components'
import type { LinkGroupItem, LinkGroupLayout } from '@mytours/components'
import { ionHistory } from '../../../navigation'
import { linksData, onLinkAction, getSectionSubtypeAndId, getSocialLink } from '../../functions'
import { getDirMode, getPlatform } from '../../../platform'
import { ImageSizePreset, ImageSizePresetMap } from 'common_lib/types'
import { generateImageUrl, generateImageSrcSet } from 'common_lib/utils'
import { SectionItemType } from '../../types'
import SectionHeader from './SectionHeader'
import '../styles/sectionGroup.less'
import '../styles/sectionLinkGroup.less'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import {
	GetCollectionItemsIndexResponseData,
	MapItem,
	GetMediaItemsShowResponse as MediaItem,
	GetScreensShowResponseSectionItems,
	BundleItem,
} from 'mytours-read-api-typescript'
import Modal from 'mobile_web/modules/common/components/modal'
import FullscreenMedia from './FullscreenMedia'
import FullscreenAr from './FullscreenAr'
import BundleRedeemDialog from './BundleRedeemDialog'
import { useBundlePurchaseReturn } from 'mobile_web/modules/lockapp/useBundlePurchaseReturn'
import SectionMediaImage from './SectionMediaImage'
import { getCollapseShadowStyle } from '../../functions'
import strings from '../../../../strings'
import '../styles/sectionCollapsable.less'
import '../styles/bundle.less'

type Props = Container.Props & Container.Actions

const TILE_LAYOUTS: LinkGroupLayout[] = ['grid_image', 'wide_image', 'horizontal_slider']

const SectionLinkGroup: React.FC<Props> = ({
	section,
	screen,
	collection,
	collectionItemsIds,
	closeModal,
	isInsideModal,
	redeemedBundles,
	validateBundleCoupon,
}) => {
	const [modalPresented, setModalPresented] = useState<boolean>(false)
	const [arModalPresented, setArModalPresented] = useState<boolean>(false)
	const [glbUrl, setGlbUrl] = useState<string>()
	const [usdzUrl, setUsdzUrl] = useState<string>()
	const [arFilename, setArFilename] = useState<string>()
	const [mediaItem, setMediaItem] = useState<MediaItem>(null)
	const [bundleDialog, setBundleDialog] = useState<{
		id: number
		accountId?: number
		purchaseValidating?: boolean
		purchasedCode?: string
		purchasedEmail?: string
		purchaseTimedOut?: boolean
	} | null>(null)
	const [isCollapsed, setCollapsed] = useState(false)
	const [blockHeight, setBlockHeight] = useState<number>()
	const contentRef = useRef<HTMLDivElement>()

	const isTabletOrMobile = useMediaQuery({ maxWidth: 1024.5 })

	useEffect(() => {
		const interval = setInterval(() => {
			if (contentRef?.current?.clientHeight !== 0 && !blockHeight && section?.collapsable) {
				setBlockHeight(contentRef?.current.clientHeight * section.exposable_content * 0.01)
				setCollapsed(section.exposable_content !== 100)
				clearInterval(interval)
			}
		}, 50)

		return () => clearInterval(interval)
	}, [contentRef?.current, section])

	// Re-validate redeemed bundles in this section whenever the screen regains focus, so a
	// backend revoke/expiry is reflected promptly (boot/metadata-sync revalidation also runs).
	useIonViewDidEnter(() => {
		section.section_items?.forEach((sectionItem: SectionItemType) => {
			if (sectionItem.item_type !== GetScreensShowResponseSectionItems.ItemTypeEnum.Bundle || !sectionItem.item_id) {
				return
			}
			const entry = redeemedBundles?.[sectionItem.item_id]
			if (entry?.hasUnlockedBundle && entry.couponUsedToUnlock) {
				validateBundleCoupon({
					devicePlatform: getPlatform(),
					coupon: entry.couponUsedToUnlock,
					id: sectionItem.item_id,
					expireAt: entry.expireAt,
				})
			}
		})
	})

	// After a Stripe purchase the store redirects back to this screen with `?order-id=`; the transient
	// bundle dialog is gone by then, so the screen catches the return on behalf of its own bundles.
	const sectionBundles = (section.section_items ?? []).filter(
		item => item.item_type === GetScreensShowResponseSectionItems.ItemTypeEnum.Bundle && !!item.item_id,
	)
	const { purchase, dismiss } = useBundlePurchaseReturn({
		bundleIds: sectionBundles.map(item => item.item_id as number),
		accountIdFor: bundleId => sectionBundles.find(item => item.item_id === bundleId)?.account_id,
	})

	const { section_items: sectionItems, layout, title, collapsable, exposable_content: exposableContent } = section

	const openModal = (sectionItem: SectionItemType): void => {
		const type = GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		const subtype = sectionItem.item_subtype
			.split('_')
			.reverse()[0] as GetCollectionItemsIndexResponseData.ItemSubtypeEnum
		NavigationService.pushModalWindow(type, subtype, sectionItem.item_id)
	}

	const onModalDidPresent = (): void => setModalPresented(true)

	const onModalDidDismiss = (): void => {
		setModalPresented(false)
		setMediaItem(null)
	}

	const onArModalDidPresent = (): void => setArModalPresented(true)

	const onArModalDidDismiss = (): void => {
		setArModalPresented(false)
		setUsdzUrl(undefined)
		setGlbUrl(undefined)
		setArFilename(undefined)
	}

	const buildItems = (sectionItems: SectionItemType[]): { linkItems: LinkGroupItem[]; bundleCards: JSX.Element[] } => {
		const linkItems: LinkGroupItem[] = []
		const bundleCards: JSX.Element[] = []
		sectionItems.forEach((sectionItem: SectionItemType) => {
			// A redeemed bundle is replaced by cards for each of its items (the link itself is dropped).
			if (sectionItem.item_type === GetScreensShowResponseSectionItems.ItemTypeEnum.Bundle && sectionItem.item_id) {
				const redeemed = redeemedBundles?.[sectionItem.item_id]
				if (redeemed?.hasUnlockedBundle && redeemed.items?.length) {
					redeemed.items.forEach(bundleItem => bundleCards.push(renderBundleItemCard(sectionItem, bundleItem)))
					return
				}
			}
			const linkItem = toLinkItem(sectionItem)
			if (linkItem) {
				linkItems.push(linkItem)
			}
		})
		return { linkItems, bundleCards }
	}

	const handleOpenAr = async (mediaItem: MediaItem) => {
		const { usdz, glb } = mediaItem
		setGlbUrl(glb?.original_url)
		setUsdzUrl(usdz?.original_url)
		setArFilename(glb?.filename || usdz?.filename)
	}

	const toTileImage = (sectionItem: SectionItemType): Pick<LinkGroupItem, 'image' | 'imageSrcSet'> => {
		// The same pair the collection list picks from, the same way round: the wide-proportioned tiles
		// take the main cover and the half-width grid tile takes the grid crop, each falling back to the
		// other rather than dropping the item to a placeholder. Which crop to ask for, not how wide the
		// library draws the tile — column count changes with the viewport and is not read here.
		const isWideTile = layout !== 'grid_image'
		const grid = sectionItem.cover_image_grid?.file
		const main = sectionItem.cover_image?.file
		// An icon chosen on the link itself wins over anything the link resolves into: it is the one
		// picture someone chose for this tile, where the covers are the target's own artwork.
		const icon = sectionItem.icon_type === 'media_item' ? sectionItem.icon?.file : undefined
		// Always generated from the file. The record may also carry a *_url_display, but nothing in the
		// app renders those — the collection list generates from the file too.
		const file = icon || (isWideTile ? main || grid : grid || main)
		if (!file) {
			return {}
		}
		const focalPoint = file.focal_point
		const options = {
			...(isWideTile ? { width: 1000, height: 563 } : ImageSizePresetMap[ImageSizePreset.Row]),
			crop: Boolean(focalPoint && (focalPoint[0] !== 0.5 || focalPoint[1] !== 0.5)),
			focalPoint,
		}
		return {
			image: generateImageUrl(file, options),
			imageSrcSet: generateImageSrcSet(file, options),
		}
	}

	// href/onClick decide which element the library renders; the URL and glyph mapping are the consumer's job.
	const toLinkItem = (sectionItem: SectionItemType): LinkGroupItem | null => {
		const { stock_icon_icon: stockIconIcon, stock_icon_style: stockIconStyle, link_type: linkType } = sectionItem
		const data = linksData[linkType]

		const isCustomIcon = stockIconIcon !== 'default'

		if (!data) {
			return null
		}

		const { subtype, id } = getSectionSubtypeAndId(sectionItem)

		const isSpecialLink = [
			GetScreensShowResponseSectionItems.LinkTypeEnum.Settings,
			GetScreensShowResponseSectionItems.LinkTypeEnum.Badges,
			GetScreensShowResponseSectionItems.LinkTypeEnum.Favourites,
		].includes(linkType)

		const href =
			linkType !== 'internal' && !isSpecialLink
				? sectionItem.url || getSocialLink(sectionItem.link_text, data.link)
				: undefined

		const isBundle =
			sectionItem.item_type === GetScreensShowResponseSectionItems.ItemTypeEnum.Bundle && !!sectionItem.item_id

		const onClickAction = isBundle
			? () => setBundleDialog({ id: sectionItem.item_id, accountId: sectionItem.account_id })
			: linkType === 'internal'
				? () => {
						if ((sectionItem.item_subtype as string) === 'media_item_ar') {
							handleOpenAr(sectionItem.media_item)
						} else if (sectionItem.item_type === MapItem.ItemTypeEnum.MediaItem) {
							setMediaItem(sectionItem.media_item)
						} else {
							onLinkAction(
								subtype,
								id,
								sectionItem.item_version,
								() => openModal(sectionItem),
								() => closeModal?.(),
								collection,
								collectionItemsIds,
								sectionItem.account_id,
							)
						}
					}
				: isSpecialLink
					? () => ionHistory.push(data.link)
					: undefined

		return {
			id: sectionItem.id,
			label: sectionItem.link_text || '',
			href,
			onClick: onClickAction,
			icon: isCustomIcon ? (
				<i className={`brand-icon fa${stockIconStyle?.charAt?.(0)} fa-${stockIconIcon}`} />
			) : (
				<i className={classNames('brand-icon', getDirMode(), data.icon)} />
			),
			...(TILE_LAYOUTS.includes(layout as LinkGroupLayout) ? toTileImage(sectionItem) : {}),
		}
	}

	const handleBundleItemPress = (parent: SectionItemType, bundleItem: BundleItem): void => {
		// Pass the long-form item_subtype straight through; onLinkAction strips the prefix itself.
		onLinkAction(
			bundleItem.item_subtype as string,
			bundleItem.item_id,
			bundleItem.item_version,
			() => undefined,
			() => closeModal?.(),
			collection,
			collectionItemsIds,
			parent.account_id,
		)
	}

	const renderBundleItemCard = (parent: SectionItemType, bundleItem: BundleItem): JSX.Element => (
		<Button
			variant="clear"
			key={`bundle-${parent.item_id}-${bundleItem.item_type}-${bundleItem.item_id}`}
			type="button"
			className="bundle-item-card"
			aria-label={bundleItem.title || ''}
			onClick={() => handleBundleItemPress(parent, bundleItem)}
		>
			<span className="bundle-item-card-thumb">
				<SectionMediaImage file={bundleItem.cover_image?.file} alt={bundleItem.title || ''} width={96} height={64} />
			</span>
			<span className="bundle-item-card-title">{bundleItem.title}</span>
			<i className="far fa-arrow-right bundle-item-card-chevron" />
		</Button>
	)

	const renderFullscreenMediaModal = (): JSX.Element => (
		<Modal
			isOpen={Boolean(mediaItem)}
			cssClass="media-modal"
			showBackdrop={true}
			onDidDismiss={onModalDidDismiss}
			onDidPresent={onModalDidPresent}
		>
			{modalPresented && (
				<FullscreenMedia mediaData={[mediaItem]} onBack={() => setMediaItem(null)} section={section} screen={screen} />
			)}
		</Modal>
	)

	const renderArModal = (): JSX.Element => (
		<Modal
			isOpen={Boolean(glbUrl) || Boolean(usdzUrl)}
			cssClass="media-modal"
			showBackdrop={true}
			onDidDismiss={onArModalDidDismiss}
			onDidPresent={onArModalDidPresent}
		>
			{arModalPresented && (
				<FullscreenAr
					usdz={usdzUrl}
					glb={glbUrl}
					filename={arFilename}
					onClose={() => {
						setGlbUrl(undefined)
						setUsdzUrl(undefined)
					}}
				/>
			)}
		</Modal>
	)

	const renderCollapseButton = (): React.ReactElement => (
		<Button onClick={() => setCollapsed(!isCollapsed)} className="collapse-btn" variant="clear" dir={getDirMode()}>
			<i className={`fas fa-caret-${isCollapsed ? 'down' : 'up'}`} />
			<span>{isCollapsed ? strings.expand : strings.collapse}</span>
		</Button>
	)

	const isFullyCollapsed = collapsable && (exposableContent === 0 || exposableContent === 100 || blockHeight < 85)

	const getCollapsedStyles = () => {
		if (isCollapsed && exposableContent !== 0) {
			return {
				maxHeight: `${exposableContent === 100 ? 34 : blockHeight}px`,
				overflow: 'hidden',
				height: `${exposableContent === 100 ? 34 : blockHeight}px`,
			}
		}
	}

	const { linkItems, bundleCards } = buildItems(sectionItems)

	return (
		<Grid
			className={classNames('section-group', `${layout.split('_').join('-')}-layout`, {
				'section-collapsable': collapsable,
				'-collapsed': isCollapsed,
				'-fully-collapsable': isFullyCollapsed,
			})}
			dir={getDirMode()}
			style={getCollapsedStyles()}
			ref={contentRef}
		>
			{!!title && <SectionHeader title={title} noOverflow={isCollapsed} />}
			{isFullyCollapsed && renderCollapseButton()}
			{((isFullyCollapsed && !isCollapsed) || !isFullyCollapsed) && (
				<Grid
					className={classNames('block-content section-group-list', getDirMode())}
					dir={getDirMode()}
					data-cy="story-screen-section-content"
				>
					<LinkGroupSection layout={layout as LinkGroupLayout} items={linkItems} dir={getDirMode()} />
					{bundleCards.length > 0 && <div className="bundle-cards">{bundleCards}</div>}
					{collapsable && exposableContent !== 0 && exposableContent !== 100 && blockHeight >= 85 && (
						<div
							className={classNames('collapse-shadow', {
								'mobile-or-tablet-collapsed': (isTabletOrMobile || isInsideModal) && isCollapsed,
							})}
							style={{ background: getCollapseShadowStyle() }}
						>
							{renderCollapseButton()}
						</div>
					)}
				</Grid>
			)}
			{mediaItem && renderFullscreenMediaModal()}
			{(glbUrl || usdzUrl) && renderArModal()}
			{(bundleDialog || purchase) && (
				<BundleRedeemDialog
					isOpen={true}
					bundleId={purchase?.bundleId ?? (bundleDialog?.id as number)}
					accountId={purchase?.accountId ?? bundleDialog?.accountId}
					collection={collection}
					collectionItemsIds={collectionItemsIds}
					purchaseValidating={purchase?.purchaseValidating}
					purchasedCode={purchase?.purchasedCode}
					purchasedEmail={purchase?.purchasedEmail}
					purchaseTimedOut={purchase?.purchaseTimedOut}
					onClose={() => {
						dismiss()
						setBundleDialog(null)
					}}
				/>
			)}
		</Grid>
	)
}

const SectionLinkGroupComponent = Container.default(SectionLinkGroup)
export default SectionLinkGroupComponent
